import { useEffect, useState } from 'react';

import type { Estatistica } from '@plane/shared';

import { ImagemDaSecao } from '@/components/imagem-da-secao';
import { TituloComDestaque } from '@/components/titulo-com-destaque';
import { useAtelie } from '@/hooks/use-conteudo';
import { prefereMenosMovimento, useRevelarAoRolar } from '@/hooks/use-revelar-ao-rolar';

/** Duração da contagem, igual à do protótipo. */
const DURACAO_MS = 1400;

/** Escalonamento entre irmãos, para o conteúdo entrar em cascata. */
const PASSO_MS = 45;

/**
 * Classes da revelação ao rolar.
 *
 * Fica numa função porque seis elementos da seção usam a mesma transição, cada
 * um com o seu atraso. O atraso vai por `style`, não por classe: são valores
 * calculados, e gerar classe dinâmica não sobrevive ao varredor do Tailwind.
 */
function revelacao(visivel: boolean): string {
  return `transition-[opacity,transform] duration-[900ms] ease-plane motion-reduce:transition-none ${
    visivel ? 'translate-y-0 opacity-100' : 'translate-y-[26px] opacity-0'
  }`;
}

/**
 * Cota sobre a foto, no desenho do protótipo.
 *
 * As duas linhas que ladeiam o texto são enfeite de projeto técnico e ficam em
 * `aria-hidden`; a metragem em si é informação e é lida.
 */
function Cota({ children, visivel }: { children: string; visivel: boolean }) {
  return (
    <figcaption
      className={`pointer-events-none absolute right-[18px] bottom-[18px] left-[18px] flex items-center gap-2 text-[0.62rem] tracking-[0.16em] text-bone uppercase transition-opacity delay-[250ms] duration-[800ms] ease-plane motion-reduce:transition-none ${
        visivel ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <span aria-hidden="true" className="h-px flex-1 bg-current opacity-85" />
      <b className="border border-bone/30 bg-brand-950/70 px-2 py-[3px] font-medium backdrop-blur-[6px]">
        {children}
      </b>
      <span aria-hidden="true" className="h-px flex-1 bg-current opacity-85" />
    </figcaption>
  );
}

/**
 * Número que conta de zero até o valor quando aparece na tela.
 *
 * O número animado fica em `aria-hidden` e o valor final vai junto do rótulo,
 * só para leitor de tela. Sem isso, quem usa leitor ouviria o número no meio da
 * contagem — "7 anos de estrada" —, que é pior do que não animar.
 */
function Numero({ estatistica, contar }: { estatistica: Estatistica; contar: boolean }) {
  const alvo = estatistica.valor;
  const sufixo = estatistica.sufixo;
  const [calmo] = useState(prefereMenosMovimento);
  const [exibido, setExibido] = useState(() => (prefereMenosMovimento() ? alvo : 0));

  useEffect(() => {
    if (!contar || calmo) return;

    let inicio: number | null = null;

    function passo(agora: number) {
      inicio ??= agora;

      const progresso = Math.min((agora - inicio) / DURACAO_MS, 1);
      // Desaceleração cúbica, a mesma curva do protótipo.
      const suave = 1 - Math.pow(1 - progresso, 3);

      setExibido(Math.round(alvo * suave));

      if (progresso < 1) quadro = requestAnimationFrame(passo);
    }

    let quadro = requestAnimationFrame(passo);

    return () => cancelAnimationFrame(quadro);
  }, [contar, calmo, alvo]);

  const completo = exibido === alvo;

  return (
    <div className="flex flex-col gap-1.5">
      <b
        aria-hidden="true"
        className="font-display text-[clamp(2.2rem,5vw,3.6rem)] leading-none font-light tracking-[-0.03em] text-brand-600 tabular-nums"
      >
        {exibido}
        {completo ? sufixo : ''}
      </b>
      <span className="text-[0.72rem] tracking-[0.14em] text-ink-soft uppercase">
        <span className="sr-only">{`${alvo}${sufixo} `}</span>
        {estatistica.rotulo}
      </span>
    </div>
  );
}

/**
 * Seção 01 — O ateliê.
 *
 * Portada de `legacy/static-site/index.html`, seção `#atelie`. O texto veio
 * literal; a grade de doze colunas e os dois pontos de quebra (980 px e
 * 1180 px) são os do protótipo, escritos como variantes de largura mínima
 * porque o Tailwind trabalha por baixo para cima.
 *
 * A revelação é um observador por seção em vez do observador global do
 * protótipo — ver `use-revelar-ao-rolar.ts`. Os números têm o seu próprio, com
 * limiar mais alto: a seção aparece bem antes deles, e contar fora da tela
 * desperdiçaria o efeito.
 */
export function AtelieSection() {
  const atelie = useAtelie();
  const { ref: refDaSecao, visivel } = useRevelarAoRolar<HTMLElement>();
  const { ref: refDosNumeros, visivel: contar } = useRevelarAoRolar<HTMLDivElement>({
    limiar: 0.6,
    margem: '0px',
  });

  return (
    <section
      ref={refDaSecao}
      id="atelie"
      className="bg-bone py-[clamp(70px,10vw,140px)]"
      aria-labelledby="atelie-titulo"
    >
      <div className="mx-auto grid w-full max-w-[1360px] grid-cols-6 items-start gap-x-[clamp(16px,2.4vw,32px)] gap-y-[clamp(24px,3vw,44px)] px-6 min-[980px]:grid-cols-12">
        <div
          className={`col-[1/7] min-[980px]:col-[1/4] min-[980px]:row-[1] ${revelacao(visivel)}`}
        >
          <p className="flex items-center gap-2.5 text-[0.72rem] font-medium tracking-[0.22em] text-brand-600 uppercase">
            <span
              aria-hidden="true"
              className="inline-block h-px w-[34px] bg-brand-600 opacity-75"
            />
            {atelie.kicker}
          </p>
        </div>

        <div
          style={{ transitionDelay: `${PASSO_MS}ms` }}
          className={`col-[1/7] min-[980px]:col-[4/13] min-[980px]:row-[1] ${revelacao(visivel)}`}
        >
          <TituloComDestaque
            id="atelie-titulo"
            texto={atelie.titulo}
            className="font-display text-[clamp(2.1rem,5.4vw,4.4rem)] leading-[0.99] tracking-[-0.025em] text-brand-950"
            destaqueClassName="font-light text-brand-600 italic"
          />
        </div>

        <div
          style={{ transitionDelay: `${PASSO_MS * 2}ms` }}
          className={`col-[1/7] min-[980px]:col-[7/13] min-[980px]:row-[2] min-[980px]:self-center min-[1180px]:col-[9/13] ${revelacao(visivel)}`}
        >
          {atelie.paragrafos.map((paragrafo, indice) => (
            <p
              key={paragrafo.slice(0, 40)}
              // O primeiro parágrafo é a entrada da seção e vem maior, como no
              // protótipo; os demais seguem o corpo de texto.
              className={
                indice === 0
                  ? 'mb-[1.1em] text-[clamp(1.02rem,0.5vw+0.92rem,1.2rem)] leading-relaxed text-ink-soft'
                  : 'mb-[1.1em] text-ink-soft'
              }
            >
              {paragrafo}
            </p>
          ))}

          <ul className="mt-[1.7rem] flex list-none flex-wrap gap-2 p-0">
            {atelie.selos.map((selo, indice) => (
              <li
                key={selo}
                style={{ transitionDelay: `${indice * PASSO_MS}ms` }}
                className={`rounded-full border border-clay px-[1em] py-[0.55em] text-[0.72rem] tracking-[0.05em] text-ink-soft transition-colors duration-300 hover:border-brand-600 hover:text-brand-600 ${revelacao(visivel)}`}
              >
                {selo}
              </li>
            ))}
          </ul>
        </div>

        <ImagemDaSecao
          mediaId={atelie.fotoPrincipal}
          alt=""
          proporcao="aspect-[5/4]"
          className={`col-[1/7] mt-[clamp(10px,2vw,26px)] min-[980px]:col-[1/7] min-[980px]:row-[2] min-[1180px]:col-[1/8] ${revelacao(visivel)}`}
        >
          {atelie.cota !== '' && <Cota visivel={visivel}>{atelie.cota}</Cota>}
        </ImagemDaSecao>

        <ImagemDaSecao
          mediaId={atelie.fotoSecundaria}
          alt=""
          proporcao="aspect-[4/5]"
          className={`col-[1/5] mt-[clamp(16px,2.5vw,34px)] min-[980px]:col-[1/6] min-[980px]:row-[3] min-[1180px]:col-[3/7] ${revelacao(visivel)}`}
        />

        <div
          ref={refDosNumeros}
          hidden={atelie.numeros.length === 0}
          className={`col-[1/7] mt-[clamp(16px,2.5vw,34px)] grid grid-cols-2 gap-[26px] border-t border-clay pt-[clamp(24px,3vw,40px)] min-[980px]:col-[7/13] min-[980px]:row-[3] min-[980px]:gap-[clamp(20px,2.6vw,36px)] min-[980px]:self-end min-[1180px]:col-[8/13] ${revelacao(visivel)}`}
        >
          {atelie.numeros.map((estatistica) => (
            <Numero key={estatistica.rotulo} estatistica={estatistica} contar={contar} />
          ))}
        </div>
      </div>
    </section>
  );
}
